import { useAuthStore } from "@/stores/authStore";
import { PRODUCTION_HOSTNAMES } from "@/consts";
import { computed } from "vue";
import { isFlagActive } from "@/helpers/shared/flags";
import { toUnix } from "@/stories/utils/dates";

type InstitutionKeys = "BakkehusetInSilkeborg" | "MollemearskInAabenraa";

type TesterConfiguration = {
  key: InstitutionKeys;
  institutionId: string;
  startAt?: number;
  endAt?: number;
};

// From this moment the new planner replaces the old one for every institution.
export const NEW_PLANNER_CUTOVER_AT = toUnix(
  new Date("2026-10-01T06:30:00+02:00"), // 1 October 06:30 Copenhagen
);

const TESTERS: TesterConfiguration[] = [
  {
    key: "BakkehusetInSilkeborg",
    institutionId: "10092",
    startAt: toUnix(new Date("2026-09-04T06:30:00+02:00")), // 4 September 06:30 Copenhagen
    endAt: toUnix(new Date("2026-09-22T06:30:00+02:00")), // 22 September 06:30 Copenhagen
  },
  {
    key: "MollemearskInAabenraa",
    institutionId: "10255",
    startAt: toUnix(new Date("2026-09-09T08:00:00+02:00")), // 9 September 08:00 Copenhagen
  },
];

export function usePlannerPermissions() {
  const authStore = useAuthStore();

  const isCutoverReached = () => toUnix(new Date()) >= NEW_PLANNER_CUTOVER_AT;

  const isNewPlannerEnabledToTester = () => {
    if (!authStore.institutionId) {
      return false;
    }

    const tester = TESTERS.find(
      (item) => item.institutionId === authStore.institutionId,
    );

    if (!tester) {
      return false;
    }

    if (tester.startAt === undefined && tester.endAt === undefined) {
      return true;
    }

    const now = toUnix(new Date());

    return (
      (tester.startAt === undefined || now >= tester.startAt) &&
      (tester.endAt === undefined || now < tester.endAt)
    );
  };

  const viewNew = computed(() => {
    if (isCutoverReached()) {
      return true;
    }

    // Let's check the permissions on production
    if (PRODUCTION_HOSTNAMES.includes(window.location.hostname)) {
      return isNewPlannerEnabledToTester();
    }

    return isFlagActive("newPlanner");
  });

  const view = computed(() => {
    if (isCutoverReached()) {
      return false;
    }

    // On production the tester institutions only get the new planner. On
    // non-production the newPlanner flag is additive, so QA can keep both
    // planners visible side by side to compare them.
    if (
      PRODUCTION_HOSTNAMES.includes(window.location.hostname) &&
      isNewPlannerEnabledToTester()
    ) {
      return false;
    }

    return (
      authStore.currentInstitution?.settings.planner.enabledEmployees || false
    );
  });

  return { view, viewNew };
}
